import React, { useState, useEffect, useCallback } from "react";
import {
  MdSearch,
  MdAdd,
  MdEdit,
  MdDelete,
  MdScience,
  MdPublic,
  MdBusiness,
  MdKeyboardArrowLeft,
  MdKeyboardArrowRight,
  MdFirstPage,
  MdLastPage,
  MdSortByAlpha,
  MdFilterList,
  MdClear,
} from "react-icons/md";
import AffiliationFormModal from "./components/AffiliationFormModal";
import affiliationService from "../../../service/affiliation.service";
import Swal from "sweetalert2";

// Hook personnalisé pour le debounce
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// Composant pour le header avec statistiques
const StatisticsHeader = ({ onCreateNew }) => (
  <div className="mb-2 flex flex-col gap-4 lg:mb-2 lg:flex-row lg:items-start lg:justify-between lg:gap-6">
    <div className="flex-1">
      <div className="mb-2 flex flex-col gap-4 sm:flex-row sm:items-center">
        <div className="min-w-0">
          <h1 className="text-md sm:text-md mb-2 font-bold text-gray-800 lg:text-xl">
            Gestion des Affiliations Scientifiques
          </h1>
        </div>
      </div>
    </div>
    <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:gap-4">
      <button
        onClick={onCreateNew}
        className="hover: flex items-center justify-center space-x-2 rounded-xl bg-gradient-to-r from-[#57A863] to-[#3d9448] px-2 py-2 font-semibold  text-white transition-all duration-300 hover:from-[#3d9448] hover:to-[#2d7233] sm:px-6 lg:space-x-3 lg:rounded-xl lg:px-2 lg:py-2"
      >
        <MdAdd className="text-lg lg:text-xl" />
        <span className="text-sm lg:text-base">Nouvelle Affiliation</span>
      </button>
    </div>
  </div>
);

// Composant pour les filtres
const FilterSection = ({
  searchText,
  setSearchText,
  showFilters,
  setShowFilters,
  filters,
  setFilters,
  countries,
  types,
  clearFilters,
  hasActiveFilters,
}) => (
  <div className="border-b border-gray-100 p-4 sm:p-6">
    <div className="mb-4 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
      <div className="w-full lg:max-w-md lg:flex-1">
        <div className="relative">
          <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 transform text-lg text-gray-400 sm:left-4 sm:text-xl" />
          <input
            type="text"
            placeholder="Rechercher une affiliation..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="focus:border-transparent w-full rounded-xl border border-gray-200 bg-gray-50 py-3 pl-10 pr-4 text-sm text-gray-700 placeholder-gray-400 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#57A863] sm:py-4 sm:pl-12 sm:text-base lg:rounded-2xl"
          />
          {searchText && (
            <button
              onClick={() => setSearchText("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 transform text-gray-400 hover:text-gray-600 sm:right-4"
            >
              <MdClear className="text-lg sm:text-xl" />
            </button>
          )}
        </div>
      </div>
      <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:gap-4">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center justify-center space-x-2 rounded-lg border px-3 py-2.5 transition-all duration-200 sm:rounded-xl sm:px-4 sm:py-3 ${showFilters
              ? "border-[#57A863]/30 bg-[#57A863]/10 text-[#57A863]"
              : "border-gray-200 bg-gray-50 text-gray-600 hover:bg-gray-100"
            }`}
        >
          <MdFilterList className="text-base sm:text-lg" />
          <span className="text-sm font-medium sm:text-base">Filtres</span>
        </button>

        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="flex items-center justify-center space-x-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-red-600 transition-all duration-200 hover:bg-red-100 sm:rounded-xl sm:px-4 sm:py-3"
          >
            <MdClear className="text-base sm:text-lg" />
            <span className="text-sm font-medium sm:text-base">Effacer</span>
          </button>
        )}
      </div>
    </div>
    {showFilters && (
      <div className="flex flex-col gap-4 rounded-xl bg-gray-50 p-4 sm:flex-row lg:rounded-2xl">
        <div className="flex-1">
          <label
            htmlFor="country-filter"
            className="mb-2 block text-sm font-medium text-gray-700"
          >
            Filtrer par pays
          </label>
          <select
            id="country-filter"
            value={filters.country}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, country: e.target.value }))
            }
            className="focus:border-transparent w-full rounded-lg border border-gray-200 bg-white p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#57A863] sm:rounded-xl sm:p-3 sm:text-base"
          >
            <option value="">Tous les pays</option>
            {countries.map((country) => (
              <option key={country} value={country}>
                {country}
              </option>
            ))}
          </select>
        </div>
        <div className="flex-1">
          <label
            htmlFor="type-filter"
            className="mb-2 block text-sm font-medium text-gray-700"
          >
            Filtrer par type
          </label>
          <select
            id="type-filter"
            value={filters.type}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, type: e.target.value }))
            }
            className="focus:border-transparent w-full rounded-lg border border-gray-200 bg-white p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#57A863] sm:rounded-xl sm:p-3 sm:text-base"
          >
            <option value="">Tous les types</option>
            {types.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>
      </div>
    )}
  </div>
);

// Composant pour l'état de chargement
const LoadingState = () => (
  <div className="flex items-center justify-center py-12 sm:py-16">
    <div className="flex flex-col items-center space-y-3 sm:flex-row sm:space-x-4 sm:space-y-0">
      <div className="border-t-transparent h-6 w-6 animate-spin rounded-full border-4 border-[#57A863] sm:h-8 sm:w-8"></div>
      <p className="text-center text-lg font-medium text-gray-600 sm:text-xl">
        Chargement des affiliations...
      </p>
    </div>
  </div>
);

// Composant pour l'état vide
const EmptyState = ({ searchText, filters, onCreateNew }) => {
  const hasFilters = searchText || filters.country || filters.type;
  return (
    <div className="flex flex-col items-center justify-center px-4 py-12 text-center sm:py-16">
      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100 sm:h-20 sm:w-20">
        <MdScience className="text-2xl text-gray-400 sm:text-3xl" />
      </div>
      <h3 className="mb-2 text-lg font-semibold text-gray-800 sm:text-xl">
        Aucune affiliation trouvée
      </h3>
      <p className="mb-6 max-w-md text-sm text-gray-600 sm:text-base">
        {hasFilters
          ? "Aucun résultat ne correspond à vos critères de recherche."
          : "Commencez par ajouter votre première affiliation scientifique."}
      </p>
      <button
        onClick={onCreateNew}
        className="flex items-center space-x-2 rounded-lg bg-[#57A863] px-4 py-2.5 font-medium text-white transition-colors duration-200 hover:bg-[#3d9448] sm:rounded-xl sm:px-6 sm:py-3"
      >
        <MdAdd className="text-base sm:text-lg" />
        <span className="text-sm sm:text-base">Ajouter une affiliation</span>
      </button>
    </div>
  );
};

// Composant principal
const Affiliation = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [affiliations, setAffiliations] = useState([]);
  const [editingAffiliation, setEditingAffiliation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({ country: "", type: "" });
  const [sortInfo, setSortInfo] = useState({ field: "name_aff", order: "asc" });
  const [countries, setCountries] = useState([]);
  const [types, setTypes] = useState([]);

  const debouncedSearchText = useDebounce(searchText, 300);

  // Récupération des données paginées depuis le backend
  const fetchAffiliations = useCallback(async () => {
    setLoading(true);
    try {
      const res = await affiliationService.getAll({
        page: currentPage,
        limit: pageSize,
        search: debouncedSearchText,
        country_aff: filters.country,
        type_aff: filters.type,
        order_by: sortInfo.field,
        order_direction: sortInfo.order,
      });
      const paged = res.data;
      setAffiliations(paged.data || []);
      setTotalRecords(paged.total || 0);
      setTotalPages(paged.last_page || 1);

      // Pour lister les filtres dynamiques, uniquement sur la page courante (pour global : endpoint dédié)
      setCountries([
        ...new Set((paged.data || []).map((a) => a.country_aff).filter(Boolean)),
      ]);
      setTypes([
        ...new Set((paged.data || []).map((a) => a.type_aff).filter(Boolean)),
      ]);
    } catch (error) {
      setAffiliations([]);
      setTotalRecords(0);
      setTotalPages(1);
      console.error("Erreur lors de la récupération des affiliations:", error);
      Swal.fire({
        icon: "error",
        title: "Erreur de chargement",
        text: "Impossible de charger les affiliations. Veuillez réessayer.",
        confirmButtonColor: "#57A863",
        confirmButtonText: "Réessayer",
        customClass: {
          popup: "rounded-2xl shadow-2xl",
          confirmButton: "rounded-xl px-6 py-3 font-medium",
        },
      });
    } finally {
      setLoading(false);
    }
  }, [
    currentPage,
    pageSize,
    debouncedSearchText,
    filters.country,
    filters.type,
    sortInfo.field,
    sortInfo.order,
  ]);

  useEffect(() => {
    fetchAffiliations();
  }, [fetchAffiliations]);

  // Reset page si recherche ou filtre change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchText, filters, pageSize]);

  const handleSort = (field) => {
    setSortInfo((prev) => ({
      field,
      order: prev.field === field && prev.order === "asc" ? "desc" : "asc",
    }));
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(parseInt(newPageSize, 10));
    setCurrentPage(1);
  };

  const handleCreateNew = () => {
    setEditingAffiliation(null);
    setModalOpen(true);
  };

  const handleEdit = (affiliation) => {
    setEditingAffiliation(affiliation);
    setModalOpen(true);
  };

  const handleRefresh = () => {
    setFilters({ country: "", type: "" });
    setSearchText("");
    setSortInfo({ field: "name_aff", order: "asc" });
    setCurrentPage(1);
    fetchAffiliations();
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Confirmer la suppression",
      html: `
        <div class="text-center p-6">
          <div class="w-20 h-20 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-4">
            <svg class="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"/>
            </svg>
          </div>
          <p class="text-gray-600 mb-3 text-lg">Cette action supprimera définitivement l'affiliation.</p>
          <p class="text-gray-800 font-semibold text-lg">Cette action est irréversible.</p>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Oui, supprimer",
      cancelButtonText: "Annuler",
      customClass: {
        popup: "rounded-2xl shadow-2xl border-0",
        confirmButton: "rounded-xl px-8 py-3 font-semibold",
        cancelButton: "rounded-xl px-8 py-3 font-semibold",
      },
    });

    if (result.isConfirmed) {
      try {
        await affiliationService.deleteAffiliation(id);
        fetchAffiliations();

        Swal.fire({
          icon: "success",
          title: "Suppression réussie",
          text: "L'affiliation a été supprimée avec succès",
          confirmButtonColor: "#57A863",
          timer: 3000,
          timerProgressBar: true,
          customClass: {
            popup: "rounded-2xl shadow-2xl border-0",
          },
        });
      } catch (error) {
        console.error("Erreur lors de la suppression:", error);
        Swal.fire({
          icon: "error",
          title: "Erreur de suppression",
          text:
            error.response?.data?.message ||
            "Une erreur est survenue lors de la suppression",
          confirmButtonColor: "#ef4444",
          customClass: {
            popup: "rounded-2xl shadow-2xl border-0",
          },
        });
      }
    }
  };

  const handleFormSuccess = (data, action) => {
    setModalOpen(false);

    Swal.fire({
      icon: "success",
      title:
        action === "create" ? "Affiliation créée" : "Affiliation mise à jour",
      text: `L'affiliation a été ${action === "create" ? "créée" : "mise à jour"
        } avec succès`,
      confirmButtonColor: "#57A863",
      timer: 3000,
      timerProgressBar: true,
      customClass: {
        popup: "rounded-2xl shadow-2xl border-0",
      },
    });

    fetchAffiliations();
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingAffiliation(null);
  };

  const clearFilters = () => {
    setFilters({ country: "", type: "" });
    setSearchText("");
  };

  // Fonction pour obtenir la couleur du tag selon le type
  const getTypeColor = (type) => {
    const typeColors = {
      Université: "bg-blue-100 text-blue-800 border-blue-200",
      Institut: "bg-[#57A863]/10 text-[#57A863] border-[#57A863]/30",
      Laboratoire: "bg-purple-100 text-purple-800 border-purple-200",
      Entreprise: "bg-orange-100 text-orange-800 border-orange-200",
      "Centre de recherche": "bg-cyan-100 text-cyan-800 border-cyan-200",
      Hôpital: "bg-red-100 text-red-800 border-red-200",
    };
    return typeColors[type] || "bg-gray-100 text-gray-800 border-gray-200";
  };

  // Calculs pour pagination
  const startRecord = (currentPage - 1) * pageSize + 1;
  const endRecord = Math.min(currentPage * pageSize, totalRecords);
  const hasActiveFilters = filters.country || filters.type || searchText;

  // Rendu principal (tableau)
  const renderMainContent = () => {
    if (loading) {
      return <LoadingState />;
    }
    if (affiliations.length === 0) {
      return (
        <EmptyState
          searchText={searchText}
          filters={filters}
          onCreateNew={handleCreateNew}
        />
      );
    }
    return (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
            <tr>
              <th className="px-3 py-3 text-left sm:px-6 sm:py-4">
                <button
                  onClick={() => handleSort("name_aff")}
                  className="flex items-center space-x-1 text-xs font-semibold text-gray-700 transition-colors duration-200 hover:text-[#57A863] sm:space-x-2 sm:text-sm"
                >
                  <MdScience className="text-sm text-[#57A863] sm:text-base" />
                  <span className="hidden sm:inline">Nom de l'affiliation</span>
                  <span className="sm:hidden">Nom</span>
                  {sortInfo.field === "name_aff" && (
                    <MdSortByAlpha
                      className={`text-sm transition-transform duration-200 sm:text-lg ${sortInfo.order === "asc" ? "rotate-0" : "rotate-180"
                        }`}
                    />
                  )}
                </button>
              </th>
              <th className="px-3 py-3 text-left sm:px-6 sm:py-4">
                <button
                  onClick={() => handleSort("country_aff")}
                  className="flex items-center space-x-1 text-xs font-semibold text-gray-700 transition-colors duration-200 hover:text-[#57A863] sm:space-x-2 sm:text-sm"
                >
                  <MdPublic className="text-sm text-blue-600 sm:text-base" />
                  <span>Pays</span>
                  {sortInfo.field === "country_aff" && (
                    <MdSortByAlpha
                      className={`text-sm transition-transform duration-200 sm:text-lg ${sortInfo.order === "asc" ? "rotate-0" : "rotate-180"
                        }`}
                    />
                  )}
                </button>
              </th>
              <th className="hidden px-3 py-3 text-left sm:px-6 sm:py-4 md:table-cell">
                <button
                  onClick={() => handleSort("type_aff")}
                  className="flex items-center space-x-2 text-sm font-semibold text-gray-700 transition-colors duration-200 hover:text-[#57A863]"
                >
                  <MdBusiness className="text-purple-600" />
                  <span>Type d'organisation</span>
                  {sortInfo.field === "type_aff" && (
                    <MdSortByAlpha
                      className={`text-lg transition-transform duration-200 ${sortInfo.order === "asc" ? "rotate-0" : "rotate-180"
                        }`}
                    />
                  )}
                </button>
              </th>
              <th className="px-3 py-3 text-center sm:px-6 sm:py-4">
                <span className="text-xs font-semibold text-gray-700 sm:text-sm">
                  Actions
                </span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {affiliations.map((affiliation) => (
              <tr
                key={affiliation.id_aff}
                className="transition-colors duration-200 hover:bg-[#57A863]/5"
              >
                <td className="px-3 py-3 sm:px-6 sm:py-4">
                  <div className="flex items-center space-x-2 sm:space-x-3">
                    <div className="h-2 w-2 flex-shrink-0 rounded-full bg-[#57A863] sm:h-3 sm:w-3"></div>
                    <div>
                      <span className="block max-w-[150px] truncate text-sm font-medium text-gray-800 sm:max-w-xs sm:text-base">
                        {affiliation.name_aff}
                      </span>
                      <span className="mt-1 block text-xs text-gray-500 md:hidden">
                        {affiliation.type_aff}
                      </span>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-3 sm:px-6 sm:py-4">
                  <span className="inline-flex items-center rounded-full border border-blue-200 bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800 sm:px-3 sm:text-sm">
                    <MdPublic className="mr-1 text-xs sm:text-sm" />
                    <span className="hidden sm:inline">
                      {affiliation.country_aff}
                    </span>
                    <span className="sm:hidden">
                      {affiliation.country_aff?.substring(0, 3)}
                    </span>
                  </span>
                </td>
                <td className="hidden px-6 py-4 md:table-cell">
                  <span
                    className={`inline-flex items-center rounded-full border px-3 py-1 text-sm font-medium ${getTypeColor(
                      affiliation.type_aff
                    )}`}
                  >
                    <MdBusiness className="mr-1 text-sm" />
                    {affiliation.type_aff}
                  </span>
                </td>
                <td className="px-3 py-3 sm:px-6 sm:py-4">
                  <div className="flex items-center justify-center space-x-1 sm:space-x-2">
                    <button
                      onClick={() => handleEdit(affiliation)}
                      className="rounded-lg p-1.5 text-[#57A863] transition-colors duration-200 hover:bg-[#57A863]/10 sm:p-2"
                      title="Modifier l'affiliation"
                    >
                      <MdEdit className="text-base sm:text-lg" />
                    </button>
                    <button
                      onClick={() => handleDelete(affiliation.id_aff)}
                      className="rounded-lg p-1.5 text-red-600 transition-colors duration-200 hover:bg-red-100 sm:p-2"
                      title="Supprimer l'affiliation"
                    >
                      <MdDelete className="text-base sm:text-lg" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="from-slate-50 to-emerald-50 min-h-screen bg-gradient-to-br via-green-50 py-4 sm:py-6 lg:py-8">
      <div className="container mx-auto px-4">
        <StatisticsHeader onCreateNew={handleCreateNew} onRefresh={handleRefresh} />

        <div className="overflow-hidden rounded-2xl border border-white/20 bg-white/80   lg:rounded-3xl">
          <FilterSection
            searchText={searchText}
            setSearchText={setSearchText}
            showFilters={showFilters}
            setShowFilters={setShowFilters}
            filters={filters}
            setFilters={setFilters}
            countries={countries}
            types={types}
            clearFilters={clearFilters}
            hasActiveFilters={hasActiveFilters}
          />

          {renderMainContent()}

          {/* Pagination */}
          {!loading && affiliations.length > 0 && (
            <div className="border-t border-gray-100 bg-gray-50/50 px-4 py-3 sm:px-6 sm:py-4">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:space-x-4 sm:space-y-0">
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-600 sm:text-sm">
                      Lignes par page:
                    </span>
                    <select
                      value={pageSize}
                      onChange={(e) => handlePageSizeChange(e.target.value)}
                      className="rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-[#57A863] sm:text-sm"
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                  <span className="text-xs text-gray-600 sm:text-sm">
                    Affichage de {startRecord} à {endRecord} sur {totalRecords} affiliations
                  </span>
                </div>

                <div className="flex items-center justify-center space-x-1 sm:space-x-2">
                  <button onClick={() => handlePageChange(1)} disabled={currentPage === 1} className="rounded-lg border border-gray-200 p-1.5 transition-colors duration-200 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 sm:p-2" title="Première page">
                    <MdFirstPage className="text-sm sm:text-lg" />
                  </button>
                  <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="rounded-lg border border-gray-200 p-1.5 transition-colors duration-200 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 sm:p-2" title="Page précédente">
                    <MdKeyboardArrowLeft className="text-sm sm:text-lg" />
                  </button>
                  <div className="flex items-center space-x-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      if (pageNum < 1 || pageNum > totalPages) return null;
                      return (
                        <button key={pageNum} onClick={() => handlePageChange(pageNum)} className={`h-8 w-8 rounded-lg text-xs font-medium transition-colors duration-200 sm:h-10 sm:w-10 sm:text-sm ${currentPage === pageNum ? "bg-[#57A863] text-white shadow-md" : "border border-gray-200 text-gray-700 hover:bg-gray-100"}`}>
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="rounded-lg border border-gray-200 p-1.5 transition-colors duration-200 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 sm:p-2" title="Page suivante">
                    <MdKeyboardArrowRight className="text-sm sm:text-lg" />
                  </button>
                  <button onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} className="rounded-lg border border-gray-200 p-1.5 transition-colors duration-200 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 sm:p-2" title="Dernière page">
                    <MdLastPage className="text-sm sm:text-lg" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        <AffiliationFormModal
          isOpen={modalOpen}
          onClose={handleCloseModal}
          onSuccess={handleFormSuccess}
          affiliationToEdit={editingAffiliation}
        />
      </div>
    </div>
  );
};

export default Affiliation;